import { useState } from "react";
import { ExpenseCategory } from "@/types/expense";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExpenseTab } from "@/components/tabs/ExpenseTab";
import { AnalyticsTab } from "@/components/tabs/AnalyticsTab";
import { HistoryTab } from "@/components/tabs/HistoryTab";
import { motion } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, BarChart3, Users } from "lucide-react";
import { useExpenses } from "@/hooks/useExpenses";
import { useRecentItems } from "@/hooks/useRecentItems";
import { TripManager } from "../TripManager";

interface TripContentProps {
  tripParticipants: string[];
  onAddExpense: (expense: any) => void;
  onDeleteExpense: (id: string) => void;
  currentTrip?: {
    id: string;
    name: string;
    shareId: string;
  };
}

export const TripContent = ({ 
  tripParticipants,
  onAddExpense,
  onDeleteExpense,
  currentTrip
}: TripContentProps) => {
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | "all">("all");
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const { recentItems: recentExpenses } = useRecentItems('recentExpenses', 10);
  const { expenses } = useExpenses();

  return (
    <Tabs defaultValue="expenses" className="w-full">
      <ScrollArea className="w-full max-w-full overflow-x-auto">
        <TabsList className="grid w-full grid-cols-3 mb-4 md:mb-8 bg-gradient-card p-1 rounded-lg min-w-[300px]">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <TabsTrigger value="expenses" className="w-full flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden md:inline">Expenses</span>
              <span className="md:hidden">Exp</span>
            </TabsTrigger>
          </motion.div>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <TabsTrigger value="analytics" className="w-full flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden md:inline">Analytics</span>
              <span className="md:hidden">Ana</span>
            </TabsTrigger>
          </motion.div>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <TabsTrigger value="history" className="w-full flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden md:inline">History</span>
              <span className="md:hidden">His</span>
            </TabsTrigger>
          </motion.div>
        </TabsList>
      </ScrollArea>
      
      {currentTrip && (
        <div className="mb-4 md:mb-6">
          <TripManager 
            expenses={expenses}
            tripName={currentTrip.name}
            shareId={currentTrip.shareId}
          />
        </div>
      )}
      
      <ScrollArea className="h-[calc(100vh-12rem)] md:h-[calc(100vh-16rem)] rounded-lg">
        <TabsContent value="expenses" className="mt-0 min-w-[300px]">
          <ExpenseTab
            expenses={expenses}
            onAddExpense={onAddExpense}
            onDeleteExpense={onDeleteExpense}
            selectedCategory={selectedCategory}
            dateRange={dateRange}
            searchQuery={searchQuery}
            onCategoryChange={setSelectedCategory}
            onDateRangeChange={setDateRange}
            onSearchChange={setSearchQuery}
            tripParticipants={tripParticipants}
          />
        </TabsContent>
        <TabsContent value="analytics" className="mt-0 min-w-[300px]">
          <AnalyticsTab
            expenses={expenses}
            recentExpenses={recentExpenses}
            tripParticipants={tripParticipants}
          />
        </TabsContent>
        <TabsContent value="history" className="mt-0 min-w-[300px]">
          <HistoryTab 
            expenses={expenses}
            tripParticipants={tripParticipants}
          />
        </TabsContent>
      </ScrollArea>
    </Tabs>
  );
};